import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import {
  clearAuthStorage,
  getAuthToken,
  getParsedStoredUser,
  syncAuthStorage,
} from "./utils/authStorage";

const AUTH_VALIDATION_CACHE_KEY = "authValidationCache";
const AUTH_VALIDATION_TTL_MS = 5 * 60 * 1000;

const getToken = () => {
  return getAuthToken();
};

const getUserRole = () => {
  syncAuthStorage();

  const storedRole = sessionStorage.getItem("userRole") || localStorage.getItem("userRole");
  if (storedRole) {
    return storedRole;
  }

  const parsedUser = getParsedStoredUser();
  if (!parsedUser) {
    return null;
  }

  return parsedUser?.role_id ? String(parsedUser.role_id) : null;
};

const getCachedValidation = (token) => {
  if (!token) {
    return null;
  }

  const rawCache = sessionStorage.getItem(AUTH_VALIDATION_CACHE_KEY);
  if (!rawCache) {
    return null;
  }

  try {
    const cache = JSON.parse(rawCache);
    const isExpired =
      !cache?.checkedAt || Date.now() - Number(cache.checkedAt) > AUTH_VALIDATION_TTL_MS;

    if (cache?.token !== token || isExpired) {
      sessionStorage.removeItem(AUTH_VALIDATION_CACHE_KEY);
      return null;
    }

    return typeof cache.isValid === "boolean" ? cache.isValid : null;
  } catch (error) {
    sessionStorage.removeItem(AUTH_VALIDATION_CACHE_KEY);
    return null;
  }
};

const setCachedValidation = (token, isValid) => {
  if (!token) {
    return;
  }

  sessionStorage.setItem(
    AUTH_VALIDATION_CACHE_KEY,
    JSON.stringify({
      token,
      isValid: Boolean(isValid),
      checkedAt: Date.now(),
    })
  );
};

const clearCachedValidation = () => {
  sessionStorage.removeItem(AUTH_VALIDATION_CACHE_KEY);
};

const PrivateRoute = ({ children, allowedRoles }) => {
  const [isValid, setIsValid] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    let isMounted = true;

    const checkToken = async () => {
      const token = getToken();

      if (!token) {
        clearCachedValidation();
        clearAuthStorage();
        if (isMounted) {
          setIsValid(false);
        }
        return;
      }

      const cachedValidation = getCachedValidation(token);
      if (cachedValidation !== null) {
        if (isMounted) {
          setIsValid(cachedValidation);
        }
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/check-token`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const { valid } = response.data;
        setCachedValidation(token, valid);
        if (!valid) {
          clearAuthStorage();
        }
        if (isMounted) {
          setIsValid(valid);
        }
      } catch (error) {
        console.error("Error checking token:", error);
        clearCachedValidation();
        clearAuthStorage();
        if (isMounted) {
          setIsValid(false);
        }
      }
    };

    checkToken();

    return () => {
      isMounted = false;
    };
  }, [API_URL]);

  if (isValid === null) {
    return <div>Loading...</div>;
  }

  if (!isValid) {
    return <Navigate to="/" />;
  }

  const userRole = getUserRole();
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" />;
  }

  return children;
};

export default PrivateRoute;
